import { memo, useEffect, useMemo, useState } from "react";
import Modal from "@/components/Modal/Modal";
import { Checkbox, MenuItem, TextField, Typography } from "@mui/material";
import styles from "./MultiAssign.module.scss";
import type { ControllerRenderProps } from "react-hook-form";

interface MultiAssignProps<T> {
  field: ControllerRenderProps<any, any>;
  label: string;
  type?: string;
  list: T[];
  idKey: keyof T;
  nameKey: keyof T;
  required?: boolean;
  error?: boolean;
  helperText?: string | undefined;
  transform?: (
    selected: T[],
    initialSelected: T[],
    fullList: T[]
  ) => unknown[];
}

const MultiAssign = <T extends Record<string, unknown>>({
  field,
  label,
  type = "",
  list,
  idKey,
  nameKey,
  required = false,
  error = false,
  helperText,
  transform,
}: MultiAssignProps<T>) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [searchText, setSearchText] = useState("");

  const initialSelected = (field.value ?? []) as T[];
  const [selected, setSelected] = useState<T[]>(initialSelected);

  useEffect(() => {
    setSelected(initialSelected);
  }, [field.value]);

  const filteredList = useMemo(() => {
    if (!searchText) return list;

    return list.filter((item) =>
      String(item[nameKey])
        .toLowerCase()
        .includes(searchText.toLowerCase())
    );
  }, [list, searchText, nameKey]);

  const toggleSelect = (item: T) => {
    const exists = selected.some(
      (x) => String(x[idKey]) === String(item[idKey])
    );

    const updated = exists
      ? selected.filter((x) => String(x[idKey]) !== String(item[idKey]))
      : [...selected, item];

    setSelected(updated);
  };

  const handleSubmit = () => {
    const payload = transform
      ? transform(selected, initialSelected, list)
      : selected;

    field.onChange(payload);
    setModalOpen(false);
  };

  const displayValue = selected.length
    ? `${selected.length} ${type ? `${type}${selected.length > 1 ? "s" : ""}` : "Selected"}`
    : "";

  return (
    <div className="flex flex-col gap-2">
      <TextField
        label={label}
        variant="standard"
        fullWidth
        value={displayValue}
        onClick={() => setModalOpen(true)}
        required={required}
        error={error}
        helperText={helperText}
        slotProps={{ input: { readOnly: true } }}
      />

      <Modal
        modalOpen={modalOpen}
        closeModal={() => setModalOpen(false)}
        heading={label}
        onSave={handleSubmit}
        saveButtonText="Done"
        width="400px"
      >
        <div className="mx-2">
          <TextField
            variant="standard"
            placeholder="Search"
            fullWidth
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />

          {filteredList && filteredList.length > 0 ? (
            filteredList.map((item) => {
              const isChecked = selected.some(
                (x) => String(x[idKey]) === String(item[idKey])
              );

              return (
                <MenuItem
                  key={String(item[idKey])}
                  onClick={() => toggleSelect(item)}
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                >
                  <div className="flex justify-between items-center w-full">
                    <span className={styles.nameStyles}>
                      {String(item[nameKey])}
                    </span>
                    <Checkbox checked={isChecked} />
                  </div>
                </MenuItem>
              );
            })
          ) : (
            <Typography align="center" mt={2}>
              No records found
            </Typography>
          )}
        </div>

      </Modal>
    </div>
  );
}

export default memo(MultiAssign) as typeof MultiAssign;